import { isFiniteNumber } from "@validereinc/utilities";
import React, {
  type CSSProperties,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuid } from "uuid";
import { PageFullScreenContainerProps } from "./types";

const cleanPaddingValue = (value?: string): number => {
  if (!value) return 0;
  if (value.includes("px")) {
    const parsedValue = parseInt(value.replace("px", ""));
    if (isFiniteNumber(parsedValue)) return parsedValue;
  }
  return 0;
};

export const PageFullScreenContainer = ({
  children,
  onFit,
  windowResizeDebounce,
  style,
  id,
  className,
  offsetRight = 0,
}: PropsWithChildren<PageFullScreenContainerProps>) => {
  const uniqueScreenId = useRef<string>(id ?? uuid());
  const [pageComponentPaddingValues, setPageComponentPaddingValues] = useState<
    number[]
  >([]);

  const resizeDebounceTimeout = useRef<ReturnType<typeof setTimeout>>();
  const hasDoneInitialResize = useRef(false);

  // Try to find <Page /> component based on role=main
  useEffect(() => {
    const element = document.getElementById(uniqueScreenId.current);
    if (element) {
      let pageComponent: HTMLElement | null = element.parentNode as HTMLElement;

      while (pageComponent && pageComponent.role !== "main") {
        pageComponent = pageComponent.parentNode as HTMLElement;
      }

      if (pageComponent) {
        const styles = window.getComputedStyle(pageComponent);
        const directions = ["top", "bottom", "left", "right"];
        const directionsPadding = directions.map((direction) =>
          cleanPaddingValue(styles.getPropertyValue(`padding-${direction}`))
        );
        setPageComponentPaddingValues(directionsPadding);
      }
    }
  }, []);

  const fitToContainer = useCallback((onFitComplete?: () => void) => {
    setTimeout(() => {
      const element = document.getElementById(uniqueScreenId.current);

      if (element) {
        const divTop = element.getBoundingClientRect().top;
        const heightToBottom = window.innerHeight - divTop;
        element.style.height = `${heightToBottom}px`;

        onFitComplete?.();
      }
    }, 1);
  }, []);

  const onWindowResize = useCallback(() => {
    if (resizeDebounceTimeout.current) {
      clearTimeout(resizeDebounceTimeout.current);
    }
    resizeDebounceTimeout.current = setTimeout(() => {
      fitToContainer(() => {
        onFit?.();
      });
    }, windowResizeDebounce ?? 100);
  }, [windowResizeDebounce, onFit, fitToContainer]);

  useEffect(() => {
    fitToContainer(() => {
      hasDoneInitialResize.current = true;
      onFit?.();
    });

    if (!hasDoneInitialResize.current)
      window.addEventListener("resize", onWindowResize, true);

    return () => {
      window.removeEventListener("resize", onWindowResize);
      if (resizeDebounceTimeout.current)
        clearTimeout(resizeDebounceTimeout.current);
    };
  }, []);

  const negativeMargins: Record<
    "marginTop" | "marginBottom" | "marginLeft" | "marginRight",
    number
  > = {
    marginTop: -(pageComponentPaddingValues.at(0) ?? 0),
    marginBottom: -(pageComponentPaddingValues.at(1) ?? 0),
    marginLeft: -(pageComponentPaddingValues.at(2) ?? 0),
    marginRight: -(pageComponentPaddingValues.at(3) ?? 0),
  };

  const componentMargin: CSSProperties = {
    ...negativeMargins,

    // Hardcoding the width of this component.
    // When width is set to calc(100% + marginRight), it looks full width.
    // But when reducing the width using calc(100% - offsetRight + marginRight),
    // an offset on the right side of the page will appear because it's not full width anymore.
    // That's what "offsetRight" is about:
    width: `calc(100% - ${offsetRight}px + ${-1 * negativeMargins.marginRight * 2}px)`,
  };

  return (
    <div
      style={{ ...style, ...componentMargin }}
      id={uniqueScreenId.current}
      className={className}
    >
      {pageComponentPaddingValues?.length >= 4 && children ? children : null}
    </div>
  );
};
